import "@/App.css";
import { useEffect, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { initAnalytics, trackPageview, markInternalUser } from "./lib/analytics";
import { useAuth } from "./hooks/useAuth";
import TrapManLanding from "./components/TrapManLanding";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AboutUs from "./components/AboutUs";
import Download from "./components/Download";
import Press from "./components/Press";
import Pitchman from "./components/Pitchman";
import KitsCatalogue from "./components/kits/KitsCatalogue";
import KitDetail from "./components/kits/KitDetail";
import KitUpload from "./components/kits/KitUpload";
import NotFound from "./components/NotFound";
import Unsubscribe from "./components/Unsubscribe";
import IgLanding from "./components/IgLanding";
import CookieBanner from "./components/CookieBanner";
import LoginPage from "./components/auth/LoginPage";
import SignupPage from "./components/auth/SignupPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import VerifyEmailPage from "./components/auth/VerifyEmailPage";
import OAuthCallback from "./components/auth/OAuthCallback";
import CheckoutSuccessPage from "./components/auth/CheckoutSuccessPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./components/admin/AdminLayout";
import OverviewPage from "./components/admin/OverviewPage";
import UsersPage from "./components/admin/UsersPage";
import UserDetailPage from "./components/admin/UserDetailPage";
import LicencesAdminPage from "./components/admin/LicencesAdminPage";
import CouponsPage from "./components/admin/CouponsPage";
import OrdersPage from "./components/admin/OrdersPage";
import AffiliatesPage from "./components/admin/AffiliatesPage";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import LicencesPage from "./components/dashboard/LicencesPage";
import DownloadPage from "./components/dashboard/DownloadPage";
import InvoicesPage from "./components/dashboard/InvoicesPage";
import AccountSettingsPage from "./components/dashboard/AccountSettingsPage";
import SiteLayout from "./components/SiteLayout";
import DocsShell from "./components/docs/DocsShell";
import "./components/docs/Docs.css";
const DocsIndex = lazy(() => import("./components/docs/DocsIndex"));
const FeaturesIndex = lazy(() => import("./components/docs/FeaturesIndex"));
const DocArticle = lazy(() => import("./components/docs/DocArticle"));

// Captures a PostHog $pageview and pushes a GTM "page_view" dataLayer
// event on every route change (see lib/analytics.js).
function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  // Admin sessions (both of us) are flagged as internal traffic so our own
  // browsing stops inflating the reports. See markInternalUser().
  useEffect(() => {
    if (user?.role === "admin") markInternalUser();
  }, [user?.role]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Shared persistent shell: the top bar (SiteNav) and the footer
                (SiteFooter) stay mounted while navigating between the public
                pages, so they look identical everywhere. */}
            <Route element={<SiteLayout />}>
              <Route path="/" element={<TrapManLanding />} />
              <Route path="/docs" element={<DocsShell />}>
                <Route index element={<DocsIndex />} />
                <Route path="features" element={<FeaturesIndex />} />
                <Route path="features/:slug" element={<DocArticle feature />} />
                <Route path=":slug" element={<DocArticle />} />
              </Route>
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/download" element={<Download />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/press" element={<Press />} />
              <Route path="/pitchman" element={<Pitchman />} />
              <Route path="/kits" element={<KitsCatalogue />} />
              <Route path="/kits/upload" element={<ProtectedRoute><KitUpload /></ProtectedRoute>} />
              <Route path="/kits/:id" element={<KitDetail />} />
              {/* Catch-all. nginx already answers unknown URLs with the
                  prerendered 404.html, but React replaces #root when it boots,
                  so without this the branded page was wiped and the visitor
                  got a blank screen. Inside SiteLayout on purpose: the static
                  404 has the nav and footer too, so the handover is seamless.
                  "*" ranks last in React Router regardless of position, so the
                  routes declared outside this group still win. */}
              <Route path="*" element={<NotFound />} />
            </Route>
            {/* Instagram ads land here. Outside SiteLayout on purpose: no
                nav and no footer, so a paid visitor has nothing to click but
                the email field (see IgLanding). */}
            <Route path="/ig" element={<IgLanding />} />
            {/* TikTok bio/ads land here: same page, its own source so the two
                platforms never blur together in the funnel numbers. */}
            <Route path="/tk" element={<IgLanding source="tk_landing" />} />
            {/* The Meta image ads land here: the creative already quotes a
                price, so this variant leads with the buy button and keeps the
                trial underneath (see buyFirst in IgLanding). The ad URLs carry
                ?cur= and ?lng= so the page shows the exact price and language
                printed on the image it was clicked from. */}
            <Route path="/igbuy" element={<IgLanding source="ig_buy" buyFirst />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<LicencesPage />} />
              <Route path="download" element={<DownloadPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="settings" element={<AccountSettingsPage />} />
            </Route>
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<OverviewPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />
              <Route path="licences" element={<LicencesAdminPage />} />
              <Route path="coupons" element={<CouponsPage />} />
              <Route path="affiliates" element={<AffiliatesPage />} />
              <Route path="orders" element={<OrdersPage />} />
            </Route>
          </Routes>
          <AnalyticsTracker />
          <CookieBanner />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
